"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";

export interface CopyToClipboardProps {
  /**
   * The value to copy to clipboard. If not provided, will use the text content of children
   */
  value?: string;
  /**
   * The content to display
   */
  children?: React.ReactNode;
  /**
   * Optional visible button label. When supplied, renders a labeled secondary button
   * rather than the compact icon-only affordance.
   */
  buttonLabel?: string;
  /**
   * Accessible name for the icon-only copy button. Ignored when `buttonLabel` is set.
   */
  copyLabel?: string;
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Size of the copy button, defaults to "sm"
   */
  size?: "sm" | "md";
  /**
   * Position of the copy icon relative to children, defaults to "right"
   */
  iconPosition?: "left" | "right";
}

type CopyStatus = "idle" | "copied" | "failed";

const STATUS_RESET_MS = 2000;
// Clearing then re-filling the live region lets screen readers re-announce an unchanged message.
const ANNOUNCE_DELAY_MS = 100;

const STATUS_MESSAGES: Record<CopyStatus, string> = { idle: "", copied: "Copied!", failed: "Copy failed" };

export function CopyToClipboard({
  value,
  children,
  buttonLabel,
  copyLabel = "Copy to clipboard",
  className,
  size = "sm",
  iconPosition = "right",
}: CopyToClipboardProps) {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const [announcement, setAnnouncement] = useState("");
  const latestCopy = useRef(0);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const announceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(resetTimer.current);
      clearTimeout(announceTimer.current);
    },
    []
  );

  const handleCopy = async () => {
    const copyId = ++latestCopy.current;
    const textToCopy = value || (typeof children === "string" ? children : "");
    let nextStatus: CopyStatus;
    try {
      if (!navigator.clipboard) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(textToCopy);
      nextStatus = "copied";
    } catch {
      nextStatus = "failed";
    }
    if (copyId !== latestCopy.current) {
      return;
    }
    setStatus(nextStatus);
    setAnnouncement("");
    clearTimeout(announceTimer.current);
    announceTimer.current = setTimeout(() => setAnnouncement(STATUS_MESSAGES[nextStatus]), ANNOUNCE_DELAY_MS);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setStatus("idle");
      setAnnouncement("");
    }, STATUS_RESET_MS);
  };

  const statusMessage = STATUS_MESSAGES[status];
  const statusRegion = (
    <span className="sr-only" role="status" aria-live="polite">
      {announcement}
    </span>
  );

  if (buttonLabel) {
    return (
      <>
        <Button
          variant="secondary"
          size={size}
          icon={status === "copied" ? "Check" : status === "failed" ? "Warning" : "Copy"}
          onPress={handleCopy}
          aria-label={buttonLabel}
          className={className}
        >
          {status === "copied" ? "Copied" : status === "failed" ? "Copy failed" : buttonLabel}
        </Button>
        {statusRegion}
      </>
    );
  }

  const iconSize = size === "sm" ? 14 : 16;
  const copyButton = (
    <Button
      variant="icon"
      size={size}
      onPress={handleCopy}
      aria-label={copyLabel}
      className="!px-2 relative"
      style={{ top: "0.5px" }}
    >
      <Icon name="Copy" size={iconSize} />
    </Button>
  );
  const tooltipButton = statusMessage ? <Tooltip content={statusMessage}>{copyButton}</Tooltip> : copyButton;

  return (
    <div className={twMerge("inline-flex items-center gap-0.5", className)}>
      {iconPosition === "left" && tooltipButton}
      {children}
      {iconPosition === "right" && tooltipButton}
      {statusRegion}
    </div>
  );
}
